import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { CLP, IVA_RATE, INVOICE_FILTER_STATUS, INVOICE_SOURCES, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, SecondaryButton, StatusBadge, EmptyState, Field, inputCls } from "@/components/ui-kit";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";
import { computeFinanceKpis } from "@/lib/financeKpis";
import InvoiceForm from "@/components/InvoiceForm";
import FinanceEntryForm from "@/components/FinanceEntryForm";
import {
  Plus, X, Upload, Trash2, ArrowUpCircle, ArrowDownCircle, ChevronLeft, ChevronRight,
  FileText, Paperclip, ExternalLink, CheckCircle2, Send, AlertTriangle, Check, Search,
} from "lucide-react";

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// DD/MM/AAAA para mostrar en tabla; formato interno ISO en BD no cambia.
function fmtDMY(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

/* ────────────────────────────── Invoice modals ────────────────────────────── */

function InvoiceDetailModal({ invoiceId, onClose, onChanged }) {
  const [inv, setInv] = useState(null);
  const [editing, setEditing] = useState({});
  const [showMarkPaid, setShowMarkPaid] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${invoiceId}`);
    setInv(data);
    setEditing({});
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  if (!inv) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white p-6 font-mono-kpi text-xs text-neutral-500">Cargando…</div>
      </div>
    );
  }

  const patch = async (payload) => {
    try {
      await api.patch(`/invoices/${invoiceId}`, payload);
      toast.success("Factura actualizada");
      await load();
      onChanged && onChanged();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      return false;
    }
  };

  const markFacturado = async () => {
    const number = window.prompt("Ingresa el N° de factura (obligatorio):", inv.invoice_number || "");
    if (!number || !number.trim()) return toast.error("N° de factura obligatorio");
    const issue = window.prompt("Fecha de emisión (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
    if (!issue) return;
    await patch({ status: "facturado", invoice_number: number.trim(), issue_date: issue });
  };

  const sendReminder = async () => {
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/send-reminder`);
      toast.success(`Recordatorio enviado a ${data.to}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async () => {
    if (!window.confirm("¿Eliminar esta factura?")) return;
    await api.delete(`/invoices/${invoiceId}`);
    toast.success("Factura eliminada");
    onChanged && onChanged();
    onClose();
  };

  const editable = editing.mode === true;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl border-t-4 border-[#FF5A00] max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Factura</div>
            <div className="font-display font-bold text-xl">
              {inv.invoice_number ? `Factura N° ${inv.invoice_number}` : "Factura sin número"}
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {inv.is_overdue && (
            <div className="border border-[#D50000]/40 bg-[#D50000]/5 p-3 flex items-center gap-2 text-sm text-[#B71C1C]">
              <AlertTriangle className="w-4 h-4" />
              <span>Factura vencida hace <b>{inv.days_overdue}</b> día(s)</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Estado</div>
              <div className="mt-1"><StatusBadge status={inv.is_overdue ? "vencido" : inv.status} /></div>
            </div>
            <div>
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Fuente</div>
              <div className="mt-1 capitalize">{INVOICE_SOURCES.find((s) => s.value === inv.source)?.label || inv.source}</div>
            </div>
            <div className="col-span-2">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Concepto</div>
              <div className="mt-1 font-medium">{inv.concept}</div>
            </div>
          </div>

          <div className="border border-neutral-200 p-4 bg-neutral-50 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">Neto</span><span className="font-mono-kpi">{CLP(inv.net_amount)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">IVA (19%)</span><span className="font-mono-kpi">{CLP(inv.iva_amount)}</span></div>
            <div className="flex justify-between pt-2 border-t border-neutral-300"><span className="font-bold">Total</span><span className="font-mono-kpi font-bold text-lg text-[#FF5A00]" data-testid="invoice-detail-gross">{CLP(inv.gross_amount)}</span></div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            {inv.issue_date && <div><div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Emisión</div><div className="mt-1 font-mono-kpi">{fmtDMY(inv.issue_date)}</div></div>}
            {inv.paid_date && <div><div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Pagada el</div><div className="mt-1 font-mono-kpi">{fmtDMY(inv.paid_date)}</div></div>}
          </div>

          {inv.customer && (
            <div className="pt-4 border-t border-neutral-100">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Cliente</div>
              <Link to={`/admin/clientes/${inv.customer.id}`} className="flex items-center justify-between p-3 border border-neutral-200 hover:border-[#FF5A00] group">
                <div className="min-w-0">
                  <div className="font-medium truncate">{inv.customer.name}</div>
                  <div className="text-xs text-neutral-500 truncate">{inv.customer.email || "sin email"}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-neutral-400 group-hover:text-[#FF5A00]" />
              </Link>
            </div>
          )}

          {inv.notes && (
            <div>
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Notas</div>
              <div className="text-sm text-neutral-700 bg-neutral-50 p-3 border border-neutral-100 whitespace-pre-wrap">{inv.notes}</div>
            </div>
          )}

          {editable && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-neutral-100 pt-4">
              <Field label="Monto NETO">
                <input type="number" defaultValue={inv.net_amount} onChange={(e) => setEditing({ ...editing, net_amount: Number(e.target.value) })} className={inputCls} data-testid="invoice-edit-net" />
              </Field>
              <Field label="Concepto">
                <input defaultValue={inv.concept} onChange={(e) => setEditing({ ...editing, concept: e.target.value })} className={inputCls} data-testid="invoice-edit-concept" />
              </Field>
              <Field label="N° Factura">
                <input defaultValue={inv.invoice_number} onChange={(e) => setEditing({ ...editing, invoice_number: e.target.value })} className={inputCls} data-testid="invoice-edit-number" />
              </Field>
              <Field label="Fecha emisión">
                <input type="date" defaultValue={(inv.issue_date || "").slice(0, 10)} onChange={(e) => setEditing({ ...editing, issue_date: e.target.value })} className={inputCls} data-testid="invoice-edit-issue" />
              </Field>
              {inv.status === "pagado" && (
                <Field label="Fecha de pago">
                  <input type="date" defaultValue={(inv.paid_date || "").slice(0, 10)} onChange={(e) => setEditing({ ...editing, paid_date: e.target.value })} className={inputCls} data-testid="invoice-edit-paid" />
                </Field>
              )}
              <div className="md:col-span-2">
                <Field label="Notas">
                  <textarea rows={2} defaultValue={inv.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className={inputCls} data-testid="invoice-edit-notes" />
                </Field>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center flex-wrap justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <button onClick={del} className="text-xs font-mono-kpi uppercase tracking-[0.15em] text-[#D50000] hover:bg-[#D50000]/5 border border-[#D50000]/30 px-3 py-2" data-testid="invoice-delete-btn">
            <Trash2 className="w-3 h-3 inline mr-1.5" />Eliminar
          </button>
          {editable ? (
            <>
              <SecondaryButton onClick={() => setEditing({})}>Cancelar</SecondaryButton>
              <PrimaryButton
                onClick={async () => {
                  const { mode, ...rest } = editing;
                  // Read current DOM values of date inputs to avoid the "user didn't fire onChange"
                  // browser edge case (e.g. change via keyboard arrow keys on some engines).
                  const issueEl = document.querySelector('[data-testid="invoice-edit-issue"]');
                  const paidEl = document.querySelector('[data-testid="invoice-edit-paid"]');
                  if (issueEl && issueEl.value) rest.issue_date = issueEl.value;
                  if (paidEl && paidEl.value) rest.paid_date = paidEl.value;
                  if (Object.keys(rest).length === 0) { setEditing({}); return; }
                  const ok = await patch(rest);
                  if (ok) setEditing({}); // Keep edit form open on failure so user can retry.
                }}
                data-testid="invoice-save-edit-btn"
              >Guardar cambios</PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton onClick={() => setEditing({ mode: true })} data-testid="invoice-edit-btn">Editar</SecondaryButton>
              {inv.status !== "pagado" && (
                <SecondaryButton onClick={sendReminder} data-testid="invoice-send-reminder">
                  <Send className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Recordatorio
                </SecondaryButton>
              )}
              {inv.status === "por_facturar" && (
                <PrimaryButton onClick={markFacturado} data-testid="invoice-mark-facturado">
                  <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Marcar Facturado
                </PrimaryButton>
              )}
              {inv.status !== "pagado" && (
                <PrimaryButton onClick={() => setShowMarkPaid(true)} data-testid="invoice-mark-pagado">
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Marcar Pagado
                </PrimaryButton>
              )}
            </>
          )}
        </div>
      </div>
      {showMarkPaid && (
        <MarkPaidModal
          invoice={inv}
          onClose={() => setShowMarkPaid(false)}
          onDone={async () => {
            setShowMarkPaid(false);
            await load();
            onChanged && onChanged();
          }}
        />
      )}
    </div>
  );
}

function MarkPaidModal({ invoice, onClose, onDone }) {
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoice_number || "");
  const [issueDate, setIssueDate] = useState(invoice.issue_date || new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const needsInvNumber = !invoice.invoice_number;
  const needsIssueDate = !invoice.issue_date;

  const submit = async () => {
    if (!paidDate) return toast.error("Fecha de pago es obligatoria");
    if (needsInvNumber && !invoiceNumber.trim()) return toast.error("N° de factura obligatorio");
    if (needsIssueDate && !issueDate) return toast.error("Fecha de emisión obligatoria");
    setSaving(true);
    try {
      const payload = { status: "pagado", paid_date: paidDate };
      if (needsInvNumber) payload.invoice_number = invoiceNumber.trim();
      if (needsIssueDate) payload.issue_date = issueDate;
      if (notes.trim()) payload.notes = (invoice.notes ? invoice.notes + "\n\n" : "") + notes.trim();
      await api.patch(`/invoices/${invoice.id}`, payload);

      // Optional receipt → attach to the auto-generated finance entry.
      if (file) {
        try {
          // Find the newly-created invoice entry (ref_id=invoice:{id})
          const [y, m] = paidDate.split("-");
          const { data: entries } = await api.get(`/finance/entries?year=${y}&month=${Number(m)}`);
          const linked = entries.find((e) => e.ref_id === `invoice:${invoice.id}`);
          if (linked) {
            const form = new FormData();
            form.append("file", file);
            await api.post(`/finance/entries/${linked.id}/receipt`, form, { headers: { "Content-Type": "multipart/form-data" } });
          }
        } catch (e) {
          console.warn("Receipt upload failed after marking invoice paid:", e?.message || e);
          toast.warning("Factura marcada como pagada, pero no se pudo adjuntar el comprobante");
        }
      }

      toast.success("Factura marcada como pagada");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg border-t-4 border-[#00C853]" data-testid="mark-paid-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#00C853]">// Marcar factura pagada</div>
            <div className="font-display font-bold text-xl">Registrar pago</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          {needsInvNumber && (
            <Field label="N° Factura *">
              <input required value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className={inputCls} data-testid="mark-paid-invoice-number" />
            </Field>
          )}
          {needsIssueDate && (
            <Field label="Fecha de emisión *">
              <input type="date" required value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inputCls} data-testid="mark-paid-issue-date" />
            </Field>
          )}
          <Field label="Fecha de pago *" hint="Puedes ingresar cualquier fecha histórica.">
            <input type="date" required value={paidDate} onChange={(e) => setPaidDate(e.target.value)} className={inputCls} data-testid="mark-paid-date-input" />
          </Field>
          <Field label="Comprobante (opcional)" hint="Imagen o PDF que se adjuntará al movimiento de ingreso.">
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm" data-testid="mark-paid-receipt" />
          </Field>
          <Field label="Notas (opcional)">
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Método de pago, banco, referencia…" data-testid="mark-paid-notes" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="mark-paid-submit"
            className="bg-[#00C853] hover:bg-[#008C3A] text-white px-5 py-2.5 font-mono-kpi text-xs uppercase tracking-[0.15em] disabled:opacity-50"
          >
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />{saving ? "Guardando…" : "Confirmar pago"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── Tabs ────────────────────────────── */

function InvoicesTab() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [view, setView] = useState(() => searchParams.get("view") === "caja" ? "caja" : "ventas"); // ventas | caja
  const [items, setItems] = useState([]);
  const [acct, setAcct] = useState(null);
  const [alertMap, setAlertMap] = useState({});
  const [filter, setFilter] = useState(() => {
    // Permite abrir Finanzas con un filtro pre-aplicado desde otras vistas (ej. Dashboard).
    const f = searchParams.get("filter");
    return ["deuda", "vencido", "facturado", "por_facturar", "pagado"].includes(f) ? f : "";
  });
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("issue_date");
  const [sortDir, setSortDir] = useState("desc");
  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  // Limpia el query param una vez consumido, para que el usuario pueda seguir cambiando filtros sin arrastre.
  useEffect(() => {
    const hasParams = searchParams.get("filter") || searchParams.get("view") || searchParams.get("tab");
    if (hasParams) {
      const next = new URLSearchParams(searchParams);
      next.delete("filter");
      next.delete("view");
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [inv, a, alerts] = await Promise.all([
        api.get("/invoices").then((r) => r.data),
        api.get(`/finance/accounting-summary?year=${year}&month=${month}`).then((r) => r.data),
        api.get("/invoices/stats/customers-with-unpaid").then((r) => r.data),
      ]);
      // Ignora la respuesta si el mes/año cambió mientras cargaba.
      if (myId !== reqIdRef.current) return;
      setItems(inv);
      setAcct(a);
      setAlertMap(alerts || {});
    } catch (err) {
      if (myId !== reqIdRef.current) return;
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };

  const searchActive = search.trim().length > 0;
  const globalMode = searchActive || !!filter; // ignora mes cuando hay búsqueda o filtro global
  const filtered = useMemo(() => {
    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    let list = items;
    // Cuando NO hay búsqueda ni filtro global activo, aplica filtro de mes por vista.
    if (!globalMode) {
      list = list.filter((i) => {
        const key = view === "caja"
          ? String(i.paid_date || "").slice(0, 7)
          : String(i.issue_date || "").slice(0, 7);
        return key === monthKey;
      });
    }
    // Filtro por estado (global — no depende del mes)
    if (filter === "por_facturar") list = list.filter((i) => i.status === "por_facturar");
    else if (filter === "facturado") list = list.filter((i) => i.status === "facturado" && !i.is_overdue);
    else if (filter === "vencido") list = list.filter((i) => i.is_overdue);
    else if (filter === "pagado") list = list.filter((i) => i.status === "pagado");
    else if (filter === "deuda") list = list.filter((i) => i.status === "facturado"); // facturado + vencido
    // Buscador (nombre cliente, RUT si presente, N° factura, concepto) sobre TODO el historial
    if (searchActive) {
      const q = search.toLowerCase();
      list = list.filter((i) =>
        (i.customer_name || "").toLowerCase().includes(q) ||
        (i.customer_rut || "").toLowerCase().includes(q) ||
        (i.invoice_number || "").toLowerCase().includes(q) ||
        (i.concept || "").toLowerCase().includes(q)
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const key = sortKey;
    return [...list].sort((a, b) => {
      const va = a[key] ?? "";
      const vb = b[key] ?? "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [items, filter, search, searchActive, globalMode, sortKey, sortDir, view, year, month]);

  // KPIs globales (no dependen de mes/vista) — separan "Deuda facturada" de "Por facturar".
  const globalKpis = useMemo(() => {
    const k = computeFinanceKpis(items);
    return {
      deuda_facturada: k.deuda_facturada.amount,
      deuda_facturada_count: k.deuda_facturada.count,
      por_facturar: k.por_facturar.amount,
      por_facturar_count: k.por_facturar.count,
    };
  }, [items]);

  // Resumen consolidado cuando la búsqueda apunta a un solo cliente.
  // Se calcula sobre TODOS los items del cliente (histórico completo), sin depender de filtros de estado.
  const customerSummary = useMemo(() => {
    if (!searchActive) return null;
    const q = search.trim().toLowerCase();
    // Encuentra clientes cuyo nombre coincide con la búsqueda.
    const matched = items.filter((i) => (i.customer_name || "").toLowerCase().includes(q));
    const ids = new Set(matched.map((i) => i.customer_id).filter(Boolean));
    if (ids.size !== 1) return null;
    const cust_id = [...ids][0];
    const list = items.filter((i) => i.customer_id === cust_id);
    const _g = (i) => Math.round(Number(i.net_amount || 0) * 1.19);
    const sum = (arr) => arr.reduce((a, i) => a + _g(i), 0);
    return {
      customer_name: list[0]?.customer_name || "Cliente",
      total_facturado: sum(list.filter((i) => i.status === "facturado" || i.status === "pagado")),
      total_pagado: sum(list.filter((i) => i.status === "pagado")),
      deuda_vencida: sum(list.filter((i) => i.is_overdue)),
      deuda_pendiente: sum(list.filter((i) => i.status === "facturado" && !i.is_overdue)),
      total_por_facturar: sum(list.filter((i) => i.status === "por_facturar")),
      count: list.length,
    };
  }, [searchActive, search, items]);

  const clearFilters = () => { setFilter(""); setSearch(""); };

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };
  const sortIcon = (k) => sortKey === k ? (sortDir === "asc" ? "▲" : "▼") : "";

  const stateLabel = (i) => {
    if (i.status === "pagado") return { label: "Pagado", cls: "bg-[#00C853]/10 text-[#008C3A] border-[#00C853]/30" };
    if (i.status === "facturado") {
      if (i.is_overdue) return { label: "Vencido", cls: "bg-[#D50000]/10 text-[#B71C1C] border-[#D50000]/30" };
      return { label: "Facturado", cls: "bg-[#2962FF]/10 text-[#1E40AF] border-[#2962FF]/30" };
    }
    return { label: "Por facturar", cls: "bg-[#FFC107]/10 text-[#B28C00] border-[#FFC107]/40" };
  };

  return (
    <>
      {/* Compact controls bar */}
      <div className="flex items-center flex-wrap justify-between gap-3 mb-5">
        {/* Month/year picker (dropdowns) */}
        <div className="flex items-center gap-1.5" data-testid="finance-month-picker">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            disabled={loading}
            data-testid="finance-month-select"
            className={`${inputCls} py-1.5 pr-8 min-w-[130px] font-medium disabled:opacity-40 disabled:cursor-wait`}
          >
            {MONTH_NAMES.map((n, i) => (
              <option key={i + 1} value={i + 1}>{n}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={loading}
            data-testid="finance-year-select"
            className={`${inputCls} py-1.5 pr-8 min-w-[95px] font-medium disabled:opacity-40 disabled:cursor-wait`}
          >
            {(() => {
              const currentYear = new Date().getFullYear();
              const years = [];
              // 2024 hasta año actual + 2
              for (let y = 2024; y <= currentYear + 2; y++) years.push(y);
              // Si el año seleccionado está fuera del rango (por navegación anterior), inclúyelo
              if (!years.includes(year)) years.unshift(year);
              return years.map((y) => <option key={y} value={y}>{y}</option>);
            })()}
          </select>
          <button onClick={prevMonth} disabled={loading} data-testid="finance-prev-month" title="Mes anterior" className="p-1.5 border border-neutral-300 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-wait"><ChevronLeft className="w-3.5 h-3.5" /></button>
          <button onClick={nextMonth} disabled={loading} data-testid="finance-next-month" title="Mes siguiente" className="p-1.5 border border-neutral-300 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-wait"><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>

        {/* Ventas | Caja toggle */}
        <div className="inline-flex border border-neutral-300">
          <button data-testid="view-ventas" onClick={() => setView("ventas")} className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] ${view === "ventas" ? "bg-[#0A0A0A] text-white" : "bg-white text-neutral-700 hover:bg-neutral-50"}`}>Ventas</button>
          <button data-testid="view-caja" onClick={() => setView("caja")} className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border-l border-neutral-300 ${view === "caja" ? "bg-[#0A0A0A] text-white" : "bg-white text-neutral-700 hover:bg-neutral-50"}`}>Caja</button>
        </div>

        <PrimaryButton onClick={() => setShowNew(true)} data-testid="invoice-new-btn"><Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Nueva factura</PrimaryButton>
      </div>

      {/* KPI Cards (compact, view-driven priority) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <KpiCard
          testid="kpi-facturado"
          label="Facturado"
          hint="Facturas emitidas en el mes (por issue_date)."
          value={CLP(acct?.facturado?.gross || 0)}
          subLabel={`Neto ${CLP(acct?.facturado?.net || 0)}`}
          count={acct?.facturado?.count || 0}
          icon={<FileText className="w-3.5 h-3.5" />}
          highlight={view === "ventas"}
          tone="blue"
          loading={loading}
        />
        <KpiCard
          testid="kpi-cobrado"
          label="Cobrado"
          hint="Facturas efectivamente pagadas en el mes (por paid_date)."
          value={CLP(acct?.cobrado?.gross || 0)}
          count={acct?.cobrado?.count || 0}
          icon={<CheckCircle2 className="w-3.5 h-3.5" />}
          highlight={view === "caja"}
          tone="green"
          loading={loading}
        />
        <KpiCard
          testid="kpi-deuda-facturada"
          label="Deuda facturada"
          hint="Facturas emitidas aún no pagadas (facturado + vencido). Global, no depende del mes. Clic para filtrar."
          value={CLP(globalKpis.deuda_facturada)}
          count={globalKpis.deuda_facturada_count}
          icon={<AlertTriangle className="w-3.5 h-3.5" />}
          highlight={filter === "deuda"}
          tone="red"
          loading={loading}
          onClick={() => { setFilter(filter === "deuda" ? "" : "deuda"); setSearch(""); }}
        />
        <KpiCard
          testid="kpi-por-facturar"
          label="Por facturar"
          hint="Trabajos aún no facturados. Global, no depende del mes. Clic para filtrar."
          value={CLP(globalKpis.por_facturar)}
          count={globalKpis.por_facturar_count}
          icon={<FileText className="w-3.5 h-3.5" />}
          highlight={filter === "por_facturar"}
          tone="neutral"
          loading={loading}
          onClick={() => { setFilter(filter === "por_facturar" ? "" : "por_facturar"); setSearch(""); }}
        />
        <KpiCard
          testid="kpi-costos-pagados"
          label="Costos pagados"
          hint="Egresos con paid_date (o date, si no tiene paid_date) en el mes."
          value={CLP(acct?.costos_pagados?.amount || 0)}
          subLabel={`Registrados ${CLP(acct?.costos_registrados?.amount || 0)}`}
          count={acct?.costos_pagados?.count || 0}
          icon={<ArrowDownCircle className="w-3.5 h-3.5" />}
          highlight={view === "caja"}
          tone="red"
          loading={loading}
        />
        <KpiCard
          testid="kpi-flujo"
          label="Flujo de caja"
          hint="Cobrado menos Costos pagados en el mes."
          value={CLP(acct?.flujo || 0)}
          icon={<TrendingIcon positive={(acct?.flujo || 0) >= 0} />}
          highlight={view === "caja"}
          tone={(acct?.flujo || 0) >= 0 ? "green" : "red"}
          loading={loading}
        />
      </div>

      {/* Filters + Search */}
      <div className="flex items-center flex-wrap justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 flex-wrap">
          <FilterBtn active={!filter} onClick={() => setFilter("")} testid="invoice-filter-all">Todas</FilterBtn>
          <FilterBtn active={filter === "vencido"} onClick={() => setFilter("vencido")} testid="invoice-filter-vencido">Vencidos</FilterBtn>
          <FilterBtn active={filter === "facturado"} onClick={() => setFilter("facturado")} testid="invoice-filter-facturado">Facturados pendientes</FilterBtn>
          <FilterBtn active={filter === "por_facturar"} onClick={() => setFilter("por_facturar")} testid="invoice-filter-por-facturar">Por facturar</FilterBtn>
          <FilterBtn active={filter === "pagado"} onClick={() => setFilter("pagado")} testid="invoice-filter-pagado">Pagados</FilterBtn>
          {(filter || searchActive) && (
            <button onClick={clearFilters} data-testid="invoice-filter-clear"
              className="ml-2 px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border border-[#FF5A00] text-[#FF5A00] hover:bg-[#FF5A00]/5 flex items-center gap-1.5">
              <X className="w-3 h-3" />Limpiar filtros
            </button>
          )}
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar en todo el histórico…" className={`${inputCls} pl-8 w-72`} data-testid="invoice-search" />
        </div>
      </div>

      {globalMode && (
        <div className="mb-4 px-3 py-2 bg-[#FF5A00]/5 border border-[#FF5A00]/30 flex items-center gap-2 text-xs" data-testid="finance-global-notice">
          <AlertTriangle className="w-3.5 h-3.5 text-[#FF5A00]" />
          <span className="text-neutral-700">Vista <b>global</b> — filtros y búsqueda ignoran el mes seleccionado. Resultados: <b>{filtered.length}</b>.</span>
        </div>
      )}

      {/* Vista consolidada por cliente cuando la búsqueda apunta a uno solo */}
      {customerSummary && (
        <div className="mb-4 border border-[#FF5A00]/40 bg-white" data-testid="customer-consolidated">
          <div className="px-4 py-2.5 bg-[#FF5A00]/5 border-b border-[#FF5A00]/30 flex items-center justify-between">
            <div>
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Resumen consolidado</div>
              <div className="font-display font-bold text-base">{customerSummary.customer_name} · <span className="font-mono-kpi text-xs text-neutral-500">{customerSummary.count} facturas históricas</span></div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 divide-x divide-neutral-200">
            <ConsolidatedItem label="Total facturado" value={CLP(customerSummary.total_facturado)} tone="blue" testid="consolidated-facturado" />
            <ConsolidatedItem label="Total pagado" value={CLP(customerSummary.total_pagado)} tone="green" testid="consolidated-pagado" />
            <ConsolidatedItem label="Deuda vencida" value={CLP(customerSummary.deuda_vencida)} tone="red" testid="consolidated-vencida" />
            <ConsolidatedItem label="Deuda pendiente" value={CLP(customerSummary.deuda_pendiente)} tone="amber" testid="consolidated-pendiente" />
            <ConsolidatedItem label="Por facturar" value={CLP(customerSummary.total_por_facturar)} tone="neutral" testid="consolidated-por-facturar" />
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-500 py-12 text-center uppercase tracking-widest">Cargando…</div>
      ) : error ? (
        <div className="border border-[#D50000]/30 bg-[#D50000]/5 p-6 text-sm text-[#B71C1C]" data-testid="finance-error">
          <AlertTriangle className="w-4 h-4 inline -mt-0.5 mr-2" />No se pudieron cargar los datos: {error}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={globalMode ? "Sin resultados" : (view === "caja" ? "Sin cobros este mes" : "Sin facturas emitidas este mes")}
          description={globalMode
            ? "No se encontraron facturas con los filtros/búsqueda actuales. Prueba con Limpiar filtros."
            : (view === "caja"
              ? `No hay facturas con fecha de pago en ${MONTH_NAMES[month - 1]} ${year}.`
              : `No hay facturas emitidas en ${MONTH_NAMES[month - 1]} ${year}.`)}
        />
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[1.6fr_100px_105px_105px_110px_120px_100px] gap-3 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">
              <button onClick={() => toggleSort("customer_name")} className="text-left hover:text-[#0A0A0A]" data-testid="sort-customer">Cliente {sortIcon("customer_name")}</button>
              <div>N°</div>
              <button onClick={() => toggleSort("issue_date")} className="text-left hover:text-[#0A0A0A]" data-testid="sort-issue">Emisión {sortIcon("issue_date")}</button>
              <button onClick={() => toggleSort("paid_date")} className="text-left hover:text-[#0A0A0A]" data-testid="sort-paid">Pago {sortIcon("paid_date")}</button>
              <button onClick={() => toggleSort("net_amount")} className="text-right hover:text-[#0A0A0A]" data-testid="sort-net">Neto {sortIcon("net_amount")}</button>
              <button onClick={() => toggleSort("gross_amount")} className="text-right hover:text-[#0A0A0A]" data-testid="sort-gross">Bruto {sortIcon("gross_amount")}</button>
              <div>Estado</div>
            </div>
            {filtered.map((i) => {
              const alertCount = alertMap[i.customer_id] || 0;
              const s = stateLabel(i);
              return (
                <div key={i.id} data-testid={`invoice-row-${i.id}`}
                  className="grid grid-cols-[1.6fr_100px_105px_105px_110px_120px_100px] gap-3 px-4 py-2.5 border-b border-neutral-100 items-center text-sm hover:bg-neutral-50/70 cursor-pointer"
                  onClick={() => setDetailId(i.id)}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {alertCount >= 2 && (
                      <span title={`${alertCount} facturas vencidas`} className="text-[#D50000] flex-shrink-0" data-testid={`invoice-alert-${i.id}`}>
                        <AlertTriangle className="w-3 h-3" />
                      </span>
                    )}
                    <span className="truncate">{i.customer_name}</span>
                  </div>
                  <div className="font-mono-kpi text-xs truncate">{i.invoice_number || <span className="text-neutral-400">—</span>}</div>
                  <div className="font-mono-kpi text-xs">{fmtDMY(i.issue_date) || <span className="text-neutral-400">—</span>}</div>
                  <div className="font-mono-kpi text-xs">{fmtDMY(i.paid_date) || <span className="text-neutral-400">—</span>}</div>
                  <div className="font-mono-kpi text-right text-xs text-neutral-500">{CLP(i.net_amount)}</div>
                  <div className="font-mono-kpi font-bold text-right text-sm">{CLP(i.gross_amount)}</div>
                  <div><span className={`font-mono-kpi text-[10px] uppercase px-2 py-0.5 border inline-block ${s.cls}`}>{s.label}</span></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showNew && <InvoiceForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detailId && <InvoiceDetailModal invoiceId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </>
  );
}

/* ── Local UI helpers used by InvoicesTab (kept in this file, small & focused) ── */
function TrendingIcon({ positive }) {
  return positive
    ? <ArrowUpCircle className="w-3.5 h-3.5" />
    : <ArrowDownCircle className="w-3.5 h-3.5" />;
}

function FilterBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border ${active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-neutral-300 hover:border-neutral-500"}`}>
      {children}
    </button>
  );
}

function KpiCard({ testid, label, hint, value, subLabel, count, icon, highlight, tone, loading, onClick }) {
  const toneCls = {
    green: "text-[#008C3A]",
    red: "text-[#B71C1C]",
    amber: "text-[#B28C00]",
    blue: "text-[#1E40AF]",
    neutral: "text-neutral-800",
  }[tone || "neutral"];
  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      title={hint || ""}
      className={`bg-white border p-3 flex flex-col text-left transition-colors ${highlight ? "border-[#FF5A00] shadow-sm ring-1 ring-[#FF5A00]/30" : "border-neutral-200"} ${clickable ? "hover:border-[#FF5A00] cursor-pointer" : ""} ${loading ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-1.5 text-neutral-500 font-mono-kpi text-[9px] uppercase tracking-[0.15em]">
        {icon}<span>{label}</span>
      </div>
      <div className={`font-mono-kpi font-bold text-lg mt-1.5 leading-tight ${toneCls}`}>{value}</div>
      {(subLabel || count != null) && (
        <div className="text-[10px] font-mono-kpi uppercase text-neutral-400 mt-1 flex items-center justify-between">
          <span>{subLabel || ""}</span>
          {count != null && <span>{count} reg.</span>}
        </div>
      )}
    </Tag>
  );
}

function ConsolidatedItem({ label, value, tone, testid }) {
  const toneCls = {
    green: "text-[#008C3A]",
    red: "text-[#B71C1C]",
    amber: "text-[#B28C00]",
    blue: "text-[#1E40AF]",
    neutral: "text-neutral-800",
  }[tone || "neutral"];
  return (
    <div className="p-3" data-testid={testid}>
      <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500">{label}</div>
      <div className={`font-mono-kpi font-bold text-base mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

/* ────────────────────────────── Expenses / Cash flow tab ────────────────────────────── */

function ImportCartolaModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dateCol, setDateCol] = useState("fecha");
  const [descCol, setDescCol] = useState("descripcion");
  const [amountCol, setAmountCol] = useState("monto");
  const [defaultType, setDefaultType] = useState("auto");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("date_col", dateCol);
      form.append("desc_col", descCol);
      form.append("amount_col", amountCol);
      form.append("default_type", defaultType);
      const { data } = await api.post("/finance/import-cartola", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importados ${data.inserted} · Omitidos ${data.skipped}`);
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl border-t-4 border-[#FF5A00]" data-testid="finance-import-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Cartola bancaria</div>
            <div className="font-display font-bold text-xl">Importar Excel/CSV</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-neutral-500 leading-relaxed">
            Sube la cartola en formato <b>.xlsx</b> o <b>.csv</b>. Indica el nombre de las columnas
            (tal cual aparecen en el archivo, en minúsculas). Si la cartola trae columnas separadas
            <b> abono</b>/<b>cargo</b> se detectan automáticamente en modo Auto.
          </div>
          <Field label="Archivo (.xlsx o .csv) *">
            <input type="file" accept=".xlsx,.csv,.xls" required data-testid="finance-import-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block text-sm" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Columna fecha"><input value={dateCol} onChange={(e) => setDateCol(e.target.value)} className={inputCls} /></Field>
            <Field label="Columna descripción"><input value={descCol} onChange={(e) => setDescCol(e.target.value)} className={inputCls} /></Field>
            <Field label="Columna monto"><input value={amountCol} onChange={(e) => setAmountCol(e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Tipo de movimiento">
            <select value={defaultType} onChange={(e) => setDefaultType(e.target.value)} className={inputCls}>
              <option value="auto">Auto (detectar abono/cargo por columnas)</option>
              <option value="income">Todo como ingreso</option>
              <option value="expense">Todo como egreso</option>
            </select>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving || !file} data-testid="finance-import-submit">
            <Upload className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />{saving ? "Importando…" : "Importar cartola"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

function CashFlowTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([
      api.get(`/finance/summary?year=${year}`).then((r) => r.data),
      api.get(`/finance/entries?year=${year}&month=${month}`).then((r) => r.data),
    ]);
    setSummary(s);
    setEntries(e);
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const monthData = useMemo(() => summary?.months?.[month - 1] || { income: 0, expense: 0, net: 0 }, [summary, month]);
  const ytd = summary?.ytd || { income: 0, expense: 0, net: 0 };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      await api.delete(`/finance/entries/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadReceipt = async (id) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "image/*,application/pdf";
    inp.onchange = async () => {
      const f = inp.files?.[0];
      if (!f) return;
      const form = new FormData();
      form.append("file", f);
      try {
        await api.post(`/finance/entries/${id}/receipt`, form, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Comprobante adjuntado");
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    };
    inp.click();
  };

  const viewReceipt = (id) => window.open(`${API_BASE}/finance/entries/${id}/receipt`, "_blank");

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-neutral-200 p-5">
          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Ingresos {MONTH_NAMES[month - 1]}</div>
          <div className="font-mono-kpi font-bold text-2xl mt-2 text-[#00C853]">{CLP(monthData.income)}</div>
        </div>
        <div className="bg-white border border-neutral-200 p-5">
          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Egresos {MONTH_NAMES[month - 1]}</div>
          <div className="font-mono-kpi font-bold text-2xl mt-2 text-[#D50000]">{CLP(monthData.expense)}</div>
        </div>
        <div className="bg-white border border-neutral-200 p-5">
          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Neto {MONTH_NAMES[month - 1]}</div>
          <div className={`font-mono-kpi font-bold text-2xl mt-2 ${monthData.net >= 0 ? "text-[#0A0A0A]" : "text-[#D50000]"}`} data-testid="finance-month-net">{CLP(monthData.net)}</div>
        </div>
        <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] p-5">
          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-400">Neto acumulado {year}</div>
          <div className={`font-mono-kpi font-bold text-2xl mt-2 ${ytd.net >= 0 ? "text-white" : "text-[#FF7A6E]"}`} data-testid="finance-ytd-net">{CLP(ytd.net)}</div>
          <div className="mt-2 text-[10px] font-mono-kpi text-neutral-400 uppercase tracking-wider">+{CLP(ytd.income)} / −{CLP(ytd.expense)}</div>
        </div>
      </div>

      {summary && (
        <div className="bg-white border border-neutral-200 mb-6">
          <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 bg-neutral-50">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Flujo mensual · {year}</div>
            <div className="flex items-center gap-2">
              <button data-testid="finance-prev-year" onClick={() => setYear(year - 1)} className="p-1.5 hover:bg-white border border-neutral-200"><ChevronLeft className="w-3.5 h-3.5" /></button>
              <div className="font-mono-kpi text-xs font-bold px-2">{year}</div>
              <button data-testid="finance-next-year" onClick={() => setYear(year + 1)} className="p-1.5 hover:bg-white border border-neutral-200"><ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-1 p-4">
            {summary.months.map((m) => {
              const total = Math.max(...summary.months.flatMap((x) => [x.income, x.expense]), 1);
              const isActive = m.month === month;
              return (
                <button key={m.month} onClick={() => setMonth(m.month)} data-testid={`finance-bar-${m.month}`}
                  className={`flex flex-col items-center gap-1 px-1 py-2 border ${isActive ? "border-[#FF5A00] bg-[#FF5A00]/5" : "border-transparent hover:bg-neutral-50"}`}>
                  <div className="w-full h-24 relative flex items-end justify-center gap-0.5">
                    <div className="w-2 bg-[#00C853]/70" style={{ height: `${(m.income / total) * 100}%` }} title={`Ing ${CLP(m.income)}`} />
                    <div className="w-2 bg-[#D50000]/70" style={{ height: `${(m.expense / total) * 100}%` }} title={`Egr ${CLP(m.expense)}`} />
                  </div>
                  <div className={`font-mono-kpi text-[10px] uppercase ${isActive ? "text-[#FF5A00] font-bold" : "text-neutral-500"}`}>{MONTH_NAMES[m.month - 1].slice(0, 3)}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-bold text-lg">Movimientos · {MONTH_NAMES[month - 1]} {year}</div>
        <div className="flex items-center gap-2">
          <SecondaryButton data-testid="finance-import-btn" onClick={() => setShowImport(true)}>
            <Upload className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Importar cartola
          </SecondaryButton>
          <PrimaryButton data-testid="finance-new-btn" onClick={() => setShowNew(true)}>
            <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Nuevo movimiento
          </PrimaryButton>
        </div>
      </div>

      {entries.length === 0 ? (
        <EmptyState title="Sin movimientos este mes" description="Registra ingresos y egresos manualmente o importa la cartola del banco." />
      ) : (
        <div className="bg-white border border-neutral-200">
          <div className="grid grid-cols-[110px_2fr_1fr_120px_140px_100px] gap-3 px-5 py-3 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">
            <div>Fecha</div><div>Descripción</div><div>Categoría</div><div className="text-right">Monto</div><div>Fuente</div><div className="text-right">Acciones</div>
          </div>
          {entries.map((e) => (
            <div key={e.id} data-testid={`finance-row-${e.id}`} className="grid grid-cols-[110px_2fr_1fr_120px_140px_100px] gap-3 px-5 py-3 border-b border-neutral-100 items-center text-sm">
              <div className="font-mono-kpi text-xs">{e.date}</div>
              <div className="flex items-center gap-2 min-w-0">
                {e.type === "income"
                  ? <ArrowUpCircle className="w-4 h-4 text-[#00C853] flex-shrink-0" />
                  : <ArrowDownCircle className="w-4 h-4 text-[#D50000] flex-shrink-0" />}
                <div className="truncate">{e.description}</div>
              </div>
              <div className="text-neutral-500 text-xs truncate">{e.category || "—"}</div>
              <div className={`font-mono-kpi font-bold text-right ${e.type === "income" ? "text-[#00C853]" : "text-[#D50000]"}`}>
                {e.type === "income" ? "+" : "−"} {CLP(e.amount)}
              </div>
              <div>
                <span className="font-mono-kpi text-[10px] uppercase px-2 py-0.5 border bg-neutral-50 text-neutral-600 border-neutral-300">
                  {e.source === "cartola" ? "Cartola" : (e.source === "invoice" ? "Factura" : (e.source === "service" ? "Servicio" : "Manual"))}
                </span>
              </div>
              <div className="text-right flex items-center justify-end gap-1">
                {e.type === "expense" && (
                  e.receipt_path
                    ? <button data-testid={`finance-view-receipt-${e.id}`} onClick={() => viewReceipt(e.id)} className="text-[#00C853] hover:bg-[#00C853]/10 p-1" title="Ver comprobante"><Paperclip className="w-3.5 h-3.5" /></button>
                    : <button data-testid={`finance-upload-receipt-${e.id}`} onClick={() => uploadReceipt(e.id)} className="text-neutral-400 hover:text-[#FF5A00] p-1" title="Adjuntar comprobante"><Upload className="w-3.5 h-3.5" /></button>
                )}
                <button data-testid={`finance-delete-${e.id}`} onClick={() => remove(e.id)} className="text-neutral-400 hover:text-[#D50000] p-1" title="Eliminar">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && <FinanceEntryForm onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {showImport && <ImportCartolaModal onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); load(); }} />}
    </>
  );
}

/* ────────────────────────────── Main page ────────────────────────────── */

export default function FinancePage() {
  const [searchParams] = useSearchParams();
  // Permite abrir Finanzas con un tab pre-seleccionado desde otros módulos.
  const initialTab = searchParams.get("tab") === "cashflow" ? "cashflow" : "invoices";
  const [tab, setTab] = useState(initialTab);

  return (
    <PageContainer>
      <PageHeader
        kicker="Módulo Finanzas"
        title="Finanzas"
        subtitle="Facturación de clientes con IVA 19% automático + flujo de caja y comprobantes."
      />

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6 flex gap-6">
        <button data-testid="finance-tab-invoices" onClick={() => setTab("invoices")}
          className={`pb-3 -mb-px text-sm font-medium border-b-2 transition-colors ${tab === "invoices" ? "border-[#FF5A00] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}>
          <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Cuentas por Cobrar
        </button>
        <button data-testid="finance-tab-cashflow" onClick={() => setTab("cashflow")}
          className={`pb-3 -mb-px text-sm font-medium border-b-2 transition-colors ${tab === "cashflow" ? "border-[#FF5A00] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}>
          <ArrowUpCircle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Movimientos y Cartola
        </button>
      </div>

      {tab === "invoices" ? <InvoicesTab /> : <CashFlowTab />}
    </PageContainer>
  );
}
